import { Helmet } from "react-helmet-async";
import { Box } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import TabsMAIQ from "app/components/TabsMAIQ";
import UsersTab from "./components/user/UsersTab";
import DealersTab from "./components/dealer/DealersTab";
import TDealershipGroupTab from "./components/dealerGroup/DealershipGroupTab";
import { NotFoundPage } from "app/components/NotFoundPage";
import { PageContainer, PageHeader } from "app/components/StyledComponents";
import { BreadcrumbsComponent } from "app/components/BreadcrumbsComponent";
import { getRoutes } from "utils/auth";

type UserManagementView = "users" | "dealers" | "dealer-groups";

const viewConfig: Record<
  UserManagementView,
  { label: string; content: React.ReactNode; path: string }
> = {
  users: {
    label: "USERS",
    content: <UsersTab />,
    path: "/user-management?view=users",
  },
  dealers: {
    label: "DEALERS",
    content: <DealersTab />,
    path: "/user-management?view=dealers",
  },
  "dealer-groups": {
    label: "DEALERSHIP GROUPS",
    content: <TDealershipGroupTab />,
    path: "/user-management?view=dealer-groups",
  },
};

const validViews: UserManagementView[] = ["users", "dealers", "dealer-groups"];

const isUserManagementView = (value: string | null): value is UserManagementView => {
  return !!value && validViews.includes(value as UserManagementView);
};

const getViewFromSearch = (search: string): string | null => {
  try {
    const params = new URLSearchParams(search);
    const view = params.get("view");
    return view ? view.trim() : null;
  } catch (error) {
    console.error("Failed to parse user management view param:", error);
    return null;
  }
};

const getAllowedViews = (): UserManagementView[] => {
  try {
    const routes = getRoutes();
    return validViews.filter(view =>
      routes.some(route => route?.path === viewConfig[view].path),
    );
  } catch (error) {
    console.error("Failed to resolve allowed user management views:", error);
    return [];
  }
};

const UserManagementPage = () => {
  const history = useHistory();
  const location = useLocation();
  const allowedViews = getAllowedViews();
  const requestedView = getViewFromSearch(location.search);
  const normalizedRequestedView: UserManagementView | null = isUserManagementView(
    requestedView,
  )
    ? requestedView
    : null;
  const isRequestedViewValid = normalizedRequestedView !== null;
  const isRequestedViewAllowed = normalizedRequestedView
    ? allowedViews.includes(normalizedRequestedView)
    : false;
  const selectedView: UserManagementView | null = isRequestedViewAllowed
    ? normalizedRequestedView
    : null;

  React.useEffect(() => {
    if (allowedViews.length === 0) return;

    if (!requestedView) {
      history.replace(viewConfig[allowedViews[0]].path);
    }
  }, [allowedViews, history, requestedView]);

  const tabsData = React.useMemo(
    () =>
      allowedViews.map(view => ({
        label: viewConfig[view].label,
        content: viewConfig[view].content,
      })),
    [allowedViews],
  );

  const selectedTabIndex = selectedView ? allowedViews.indexOf(selectedView) : 0;

  const breadcrumbsList = [
    { label: "User Management", isActive: true }
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    const nextView = allowedViews[newIndex];
    if (!nextView) return;
    history.push(viewConfig[nextView].path);
  };

  if (allowedViews.length === 0) {
    return <NotFoundPage />;
  }

  if (requestedView && !isRequestedViewValid) {
    return <NotFoundPage />;
  }

  if (requestedView && isRequestedViewValid && !isRequestedViewAllowed) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Helmet>
        <title>User Management</title>
        <meta name="description" content="Manage User Informations" />
      </Helmet>
      <PageContainer
        sx={{
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <BreadcrumbsComponent breadcrumbsList={breadcrumbsList} />
        <PageHeader>User Management</PageHeader>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {allowedViews.length > 1 ? (
            <TabsMAIQ
              tabs={tabsData}
              value={Math.max(selectedTabIndex, 0)}
              onChange={handleTabChange}
              contentStyle={{
                overflow: 'visible',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            />
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {tabsData[0]?.content ?? null}
            </Box>
          )}
        </Box>
      </PageContainer>
    </>
  );
};

export default UserManagementPage;
